import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';

import { COLUMNS_MAP } from '@/utils/columns';
import { useMobileCarouselSettings } from '@/utils/useMobileCarouselSettings';

import EmblaCarousel from '../components/EmblaCarousel';
import { EmptyState } from '../ui/EmptyState';

import { computeFont } from '../utils/fonts';
import getCardBackroundImageStyles from '../utils/getCardBackroundImageStyles';
import { defaultStructure as defaultPostStructure } from './utils/defaultStructure';

import { BOX_SHADOW_VISIBILITY_MARGIN } from '../constants';
import RedirectLink from './card/RedirectLink';
import Serializer from './card/Serializer';
import * as styles from './Post.css';
import type { PostElementProps } from './Post.types';
import Pagination from './pagination';
import SearchingState from './pagination/SeachingState';
import SearchInput from './pagination/SearchInput';
import { PostSerializer } from './SerializerV2';

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};

const handleDivider = (columns: string, horizontalScroll: boolean, hasDivider: boolean) => {
  try {
    if (!hasDivider) {
      return '';
    }

    return (columns && Number.parseInt(columns, 10) > 1) || horizontalScroll
      ? styles.postGridColumn.multipleColumns
      : styles.postGridColumn.singleColumn;
  } catch {
    return '';
  }
};

export function PostElement(props: PostElementProps) {
  const [minContainerHeight, setMinContainerHeight] = useState<number | undefined>();
  const isCalculatingHeight = useRef(false);

  const {
    element,
    children,
    attributes,
    className,
    isLivePost = false,
    paginateHasMorePosts,
    paginateFetchMorePosts,
    isLoading = false,
    onSearchQueryChange,
    isSearching,
    setIsSearching,
    handleNonDebouncedSearchQuery,
    handleResetSearchState,
    onCommentClick,
    onLikeClick,
    isViewportMobile: _isViewportMobile, // we don't want this in the ...props rest since we're spreading it on a div
    ...rest
  } = props;

  const {
    // isViewportMobile, (already defined above, so we'll explicity get it from attrs)
    mobile_hide,
    desktop_hide,
    customAttributes,
    postGroupType,
    margin,
    padding,
    data,
    gap,
    mobileGap,
    shadow,
    hoverShadow,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    background,
    textAlign,
    cardPadding,

    // Divider
    dividerColor,
    dividerStyle,
    dividerThickness,

    // Image
    imageEnabled,

    // Tags
    tagTextColor,
    tagBackgroundColor,
    tagBorderRadius,
    tagBadgeEnabled,
    tagFontFamily,
    tagTransform,
    tagFontWeight,

    // Background Image
    backgroundImageFilterColor,
    hasBackgroundImage,

    // Titles
    titleTextColor,
    titleFontSize,
    titleFontWeight,
    titleLineClamp,
    titleFontFamily,
    titleTransform,
    subtitleTextColor,
    subtitleFontSize,
    subtitleLineClamp,
    subtitleFontFamily,
    subtitleTransform,
    subtitleFontWeight,

    // Hover
    hoverTitleUnderlineColor,
    hoverTitleColor,
    hoverSubtitleColor,
    hoverSubtitleUnderlineColor,

    // Authors
    authorsTextColor,
    authorsTextTransform,
    authorsFontSize,
    authorsFontFamily,
    authorsFontWeight,
    // Timestamps
    timestampsTextColor,
    timestampsFontWeight,

    // Read Time
    readTimeTextColor,

    // Premium
    premiumTextColor,
    premiumBackgroundColor,
    premiumBadgeEnabled,
    premiumFontWeight,
    // Separator
    separatorColor,

    // Search Input
    inputBackgroundColor,
    inputTextColor,
    inputPlaceholderColor,
    inputBorderRadius,
    inputBorderWidth,
    inputBorderColor,
    inputBorderStyle,
    inputEnabled,
    inputBorderFocus,
    inputIconFill,
    inputAlignment,
    inputMaxWidth,
    inputMaxHeight,
    inputMarginBottom,

    // Empty State
    emptyStateTextColor,
    emptyStateBackgroundColor,
    emptyStateText,
    emptyStatePadding,

    // Pagination
    paginationMarginTop,
    structureTiptapState,
    // structureTiptapStateDraft,
    cardStructureV2,
  } = element.attrs || {};

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: element.attrs,
    // need to be explicit here since we removed it from ...rest and should now be from element.attrs
    isViewportMobile: element.attrs?.isViewportMobile || false,
  });

  const { posts, postsInsights } = data || [];
  const dividerClass = handleDivider(
    effectiveColumns || '3',
    !!displayedShouldScrollHorizontally,
    dividerStyle !== 'none',
  );
  const hasBorder = borderStyle !== 'none';
  const postCardStyles = hasBackgroundImage
    ? hasBorder
      ? styles.postCardWithBorderAndBackgroundImage
      : styles.postCardWithBackgroundImage
    : styles.postCard;
  const isTagPage = postGroupType === 'tag_page';
  const isAuthorPage = postGroupType === 'author_page';
  const isArchive = postGroupType === 'archive';
  const canShowPagination = isArchive || isTagPage || isAuthorPage;
  const showPagination = (paginateHasMorePosts || !isLivePost) && canShowPagination;

  useEffect(() => {
    if (!isLoading && isArchive && minContainerHeight === undefined && isCalculatingHeight.current === false) {
      isCalculatingHeight.current = true;
      requestAnimationFrame(() => {
        const postCardElement = document.querySelector('.post-root-element');
        const { height } = postCardElement?.getBoundingClientRect() || {};
        if (height) {
          setMinContainerHeight(height);
        }

        isCalculatingHeight.current = false;
      });
    }
  }, [isLoading, isArchive, minContainerHeight, isCalculatingHeight]);

  const desktopSlideSize =
    displayedShouldScrollHorizontally && !!effectiveColumns ? `${100 / Number.parseInt(effectiveColumns, 10)}%` : '';

  const cardMarginBottom = useMemo(() => {
    if (!displayedShouldScrollHorizontally) {
      return undefined;
    }

    const marginBasedOnShadow = shadow ? BOX_SHADOW_VISIBILITY_MARGIN[shadow] : undefined;
    const marginBasedOnHoverShadow = hoverShadow ? BOX_SHADOW_VISIBILITY_MARGIN[hoverShadow] : undefined;

    return Math.max(marginBasedOnShadow || 0, marginBasedOnHoverShadow || 0);
  }, [shadow, hoverShadow, displayedShouldScrollHorizontally]);

  /* spreading props on standard DOM elements causes React warnings and hydration errors */
  return (
    <div
      {...rest}
      {...customAttributes}
      className="relative flex flex-col post-root-element"
      style={
        {
          width: '100%',
          minHeight:
            isArchive && minContainerHeight !== undefined && isSearching ? `${minContainerHeight}px` : undefined,
          margin,
          padding,

          '--columns': effectiveColumns,
          '--card-background': background,
          '--shadow': shadow,
          '--hover-shadow': hoverShadow,
          '--border-radius': borderRadius,

          '--text-align': textAlign,

          // Desktop Slider
          '--desktop-slide-size': desktopSlideSize,
          '--post-slider-control-background-color': displayedSliderControlsBackgroundColor,
          '--post-slider-control-text-color': displayedSliderControlsTextColor,
          '--post-slider-control-border-color': displayedSliderControlsBorderColor,

          // Mobile Slider
          '--mobile-slider-control-background-color':
            element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
          '--mobile-slider-control-text-color':
            element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
          '--mobile-slider-control-border-color':
            element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,

          // Dividers
          '--divider-color': dividerColor,
          '--divider-style': dividerStyle,
          '--divider-thickness': dividerThickness,

          // Separator
          '--separator-color': separatorColor,

          // Tags
          '--tag-text-color': tagTextColor,
          '--tag-background-color': tagBadgeEnabled ? tagBackgroundColor : 'transparent',
          '--tag-border-radius': tagBorderRadius,
          '--tag-font-family': computeFont(tagFontFamily),
          '--tag-text-transform': tagTransform,
          '--tag-font-weight': tagFontWeight,

          // Background Image
          '--background-image-filter-color': backgroundImageFilterColor,

          // Hover
          '--hover-title-underline-color': hoverTitleUnderlineColor,
          '--hover-title-color': hoverTitleColor,
          '--hover-subtitle-color': hoverSubtitleColor,
          '--hover-subtitle-underline-color': hoverSubtitleUnderlineColor,

          // Titles
          '--title-text-color': titleTextColor,
          '--title-font-size': titleFontSize,
          '--title-line-clamp': titleLineClamp,
          '--title-font-family': computeFont(titleFontFamily),
          '--title-font-weight': titleFontWeight,
          '--title-transform': titleTransform,
          '--subtitle-text-color': subtitleTextColor,
          '--subtitle-font-size': subtitleFontSize,
          '--subtitle-line-clamp': subtitleLineClamp,
          '--subtitle-font-family': computeFont(subtitleFontFamily),
          '--subtitle-transform': subtitleTransform,
          '--subtitle-font-weight': subtitleFontWeight,

          // Authors
          '--authors-text-color': authorsTextColor,
          '--authors-text-transform': authorsTextTransform,
          '--authors-font-family': computeFont(authorsFontFamily),
          '--authors-font-size': authorsFontSize,
          '--authors-font-weight': authorsFontWeight,

          // Timestamps
          '--timestamps-text-color': timestampsTextColor,
          '--timestamps-font-weight': timestampsFontWeight,

          // Read Time
          '--read-time-text-color': readTimeTextColor,

          // Premium
          '--premium-text-color': premiumTextColor,
          '--premium-background-color': premiumBadgeEnabled ? premiumBackgroundColor : 'transparent',
          '--premium-font-weight': premiumFontWeight,

          // Search Input
          '--input-background-color': inputBackgroundColor,
          '--input-text-color': inputTextColor,
          '--input-placeholder-color': inputPlaceholderColor,
          '--input-border-radius': inputBorderRadius,
          '--input-border-width': inputBorderWidth,
          '--input-border-color': inputBorderColor,
          '--input-border-style': inputBorderStyle,
          '--input-border-focus': inputBorderFocus,
          '--input-icon-fill': inputIconFill,
          '--input-max-width': inputMaxWidth,
          '--input-max-height': inputMaxHeight,
          '--input-margin-bottom': inputMarginBottom,

          // Pagination
          '--pagination-margin-top': paginationMarginTop,
        } as CSSProperties
      }
      {...attributes}
    >
      <SearchInput
        customAttributes={customAttributes}
        alignment={inputAlignment}
        onChange={onSearchQueryChange}
        isVisible={isArchive && !!inputEnabled}
        setIsSearching={setIsSearching}
        handleNonDebouncedSearchQuery={handleNonDebouncedSearchQuery}
        handleResetSearchState={handleResetSearchState}
      />

      <div
        className={classnames(
          className,
          {
            'mobile:hidden': mobile_hide,
            'desktop:hidden': desktop_hide,
          },
          customAttributes?.className,
          'relative',
          styles.postsGrid,
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
        style={
          {
            '--gap': gap,
            '--mobile-gap': mobileGap !== undefined ? mobileGap : gap,
            width: '100%',
            margin,
            padding,
          } as CSSProperties
        }
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive={isLivePost}
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {posts?.map((post) => {
            if (cardStructureV2) {
              return (
                <div
                  key={post.id}
                  className={classnames(dividerClass, 'embla__slide relative', isLivePost && 'cursor-pointer')}
                  style={{
                    // Add a margin at the bottom of the Post card to avoid cutting-off the shadow
                    marginBottom: cardMarginBottom,
                  }}
                >
                  {isLivePost ? (
                    <RedirectLink to={`/p/${post.slug}`} className="relative z-10 h-full embla__slide__number">
                      <PostSerializer
                        key={post.id}
                        node={(structureTiptapState?.content?.[0] || defaultPostStructure.content[0]) as any}
                        rootClassName={'h-full'}
                        rootStyle={{}}
                        post={post}
                        postInsights={postsInsights?.[post.id]}
                        isLive={isLivePost}
                        onLikeClick={onLikeClick}
                        onCommentClick={onCommentClick}
                        postNode={element}
                      />
                    </RedirectLink>
                  ) : (
                    <PostSerializer
                      key={post.id}
                      node={(structureTiptapState?.content?.[0] || defaultPostStructure.content[0]) as any}
                      rootClassName={'h-full'}
                      rootStyle={{}}
                      post={post}
                      postInsights={postsInsights?.[post.id]}
                      isLive={isLivePost}
                      onLikeClick={onLikeClick}
                      onCommentClick={onCommentClick}
                      postNode={element}
                    />
                  )}
                </div>
              );
            }

            return (
              <div
                key={post.id}
                className={classnames(dividerClass, 'embla__slide relative')}
                style={{
                  // Add a margin at the bottom of the Post card to avoid cutting-off the shadow
                  marginBottom: cardMarginBottom,
                }}
              >
                <div
                  className={classnames(
                    'h-full group',
                    hasBorder && 'overflow-hidden relative',
                    postCardStyles,
                    isLivePost && 'cursor-pointer	',
                    hoverShadow && 'transition-all duration-300 ease-in-out',
                  )}
                  style={{
                    ...(borderRadius && {
                      borderRadius,
                      overflow: 'hidden',
                    }),
                    borderWidth,
                    borderColor,
                    borderStyle,
                    padding: cardPadding,
                    ...(imageEnabled ? getCardBackroundImageStyles(post.image_url, hasBackgroundImage || false) : {}),
                  }}
                >
                  {isLivePost ? (
                    <RedirectLink to={`/p/${post.slug}`} className="relative z-10 embla__slide__number">
                      <Serializer key={post.id} post={post} attributes={element.attrs} />
                    </RedirectLink>
                  ) : (
                    <Serializer key={post.id} post={post} attributes={element.attrs} />
                  )}
                </div>
              </div>
            );
          })}
        </EmblaCarousel>
      </div>

      {isSearching && (
        <SearchingState
          style={
            {
              gap: gap,
            } as CSSProperties
          }
          className={classnames(
            styles.postsGrid,
            displayedShouldScrollHorizontally ? 'flex' : 'grid',
            !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
          )}
        />
      )}

      {!isLoading && !isSearching && (!posts || posts.length === 0) ? (
        <EmptyState
          padding={emptyStatePadding}
          backgroundColor={emptyStateBackgroundColor}
          textColor={emptyStateTextColor}
        >
          {emptyStateText}
        </EmptyState>
      ) : null}

      {/** We pass this value in from orchid which will handle the logic around pagination */}
      {showPagination && (
        <Pagination
          type={'loadMore'}
          onLoadMore={paginateFetchMorePosts}
          customAttributes={customAttributes}
          attributes={{
            paginateButtonBorderRadius: element.attrs.paginateButtonBorderRadius,
            paginateButtonBorderWidth: element.attrs.paginateButtonBorderWidth,
            paginateButtonBorderStyle: element.attrs.paginateButtonBorderStyle,
            paginateButtonBorderColor: element.attrs.paginateButtonBorderColor,
            paginateButtonPadding: element.attrs.paginateButtonPadding,
            paginateButtonBackgroundColor: element.attrs.paginateButtonBackgroundColor,
            paginateButtonTextColor: element.attrs.paginateButtonTextColor,
            paginateButtonHoverBackgroundColor: element.attrs.paginateButtonHoverBackgroundColor,
            paginateButtonHoverTextColor: element.attrs.paginateButtonHoverTextColor,
            paginateButtonShadow: element.attrs.paginateButtonShadow,
            paginateButtonIconColor: element.attrs.paginateButtonIconColor,
            paginateButtonIconPlacement: element.attrs.paginateButtonIconPlacement,
            paginateButtonIcon: element.attrs.paginateButtonIcon,
            paginateButtonText: element.attrs.paginateButtonText,
            paginateButtonBusyText: element.attrs.paginateButtonBusyText,
            paginationMarginTop: element.attrs.paginationMarginTop,
          }}
          isBusy={isLoading}
          isLive={isLivePost}
        />
      )}

      {children}
    </div>
  );
}
